/**
 * Book or correct an hour.
 *
 * Time is entered in hours and sent in minutes, and the conversion happens once
 * here. Everything downstream — the week total, the invoice line, the
 * utilisation report — works in integer minutes, so there is no float in the
 * chain at all.
 *
 * The entry is always the signed-in person's. There is no "who" field: booking
 * somebody else's time is not a correction, it is a different record, and the
 * API resolves the employee from the session rather than the body.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Alert,
  ApiError,
  Button,
  Input,
  Modal,
  RecordSelect,
  Select,
  Textarea,
  api,
  toast,
  todayISO,
} from '@connect/web-shared';

const schema = z.object({
  entry_date: z.string().min(1, 'Which day was this?'),
  hours: z
    .string()
    .trim()
    .regex(/^\d{1,2}(\.\d{1,2})?$/, 'Hours, to two decimals — 1.5 for ninety minutes.')
    .refine((value) => Number(value) > 0, 'More than nothing.')
    .refine((value) => Number(value) <= 24, 'A day is 24 hours.'),
  notes: z.string().trim().max(500).optional(),
});

type FormValues = z.infer<typeof schema>;

interface EntryRecord {
  public_id: string;
  entry_date: string;
  minutes: number;
  project_public_id: string;
  project_name: string;
  task_title: string | null;
  notes: string | null;
  locked: boolean;
}

interface TaskOption {
  public_id: string;
  title: string;
}

// A FUNCTION, not a constant. A module-level object would capture `todayISO()`
// once, when the bundle loads — so a console left open overnight would go on
// offering yesterday's date, and the entry would be filed against the wrong
// day by somebody who never looked at the field.
const DEFAULTS = (): FormValues => ({
  entry_date: todayISO(),
  hours: '',
  notes: '',
});

export function TimeEntryDialog({
  open,
  onClose,
  onSaved,
  entry,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: (publicId: string) => void;
  /** The entry being corrected, or `null` to book new time. */
  entry: EntryRecord | null;
}) {
  const queryClient = useQueryClient();
  const editing = entry !== null;
  const [project, setProject] = useState<string | null>(null);
  const [task, setTask] = useState<string | null>(null);

  const tasks = useQuery({
    queryKey: ['tasks', 'for-project', project],
    queryFn: async () => {
      const page = await api.page<TaskOption>('/tasks', {
        params: { page_size: 100, project: project ?? undefined, sort: 'title:asc' },
      });
      return page.rows;
    },
    enabled: open && project !== null,
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS() });

  useEffect(() => {
    if (!open) return;
    if (entry) {
      form.reset({
        entry_date: entry.entry_date,
        hours: String(entry.minutes / 60),
        notes: entry.notes ?? '',
      });
      setProject(entry.project_public_id);
    } else {
      form.reset(DEFAULTS());
      setProject(null);
      setTask(null);
    }
  }, [open, entry, form]);

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        entry_date: values.entry_date,
        // Rounded to the minute here, once. Everything downstream is an integer.
        minutes: Math.round(Number(values.hours) * 60),
        notes: values.notes?.trim() || null,
        task_id: task,
        ...(editing ? {} : { project_id: project }),
      };
      if (editing) return api.patch<EntryRecord>(`/time-entries/${entry.public_id}`, payload);
      return api.post<EntryRecord>('/time-entries', payload);
    },
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success(editing ? 'Entry updated' : 'Time booked');
      onSaved?.(record.public_id);
      if (editing) onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS()) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => {
    if (!editing && project === null) {
      form.setError('root', { message: 'Choose the project this time is against.' });
      return;
    }
    void form.handleSubmit((values) => save.mutateAsync(values))();
  };

  const frozen = entry?.locked ?? false;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit entry' : 'Book time'}
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={save.isPending} onClick={submit} disabled={frozen}>
            {editing ? 'Save changes' : 'Book it'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        {frozen ? (
          <Alert tone="warning" title="This week is frozen">
            It has been submitted for approval, so its entries cannot be changed. An approver can
            send the week back if it needs correcting.
          </Alert>
        ) : null}

        {editing ? (
          <Alert tone="info">
            Against {entry.project_name}. Time cannot be moved between projects — book it again
            on the right one and remove this.
          </Alert>
        ) : (
          <RecordSelect
            label="Project"
            required
            endpoint="/projects"
            value={project}
            onChange={(next) => {
              setProject(next);
              setTask(null);
            }}
            toOption={(row) => ({
              value: String(row.public_id),
              label: String(row.name),
              detail: String(row.client_name ?? ''),
            })}
            placeholder="Search projects…"
            disabled={frozen}
          />
        )}

        <Select
          label="Task"
          options={[
            { value: '', label: 'No specific task' },
            ...(tasks.data ?? []).map((t) => ({ value: t.public_id, label: t.title })),
          ]}
          value={task ?? ''}
          onChange={(event) => setTask(event.target.value || null)}
          hint={
            project === null
              ? 'Choose a project first.'
              : 'A non-billable task makes this entry non-billable, whatever the project says.'
          }
          disabled={project === null || frozen}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Date"
            type="date"
            required
            disabled={frozen}
            error={form.formState.errors.entry_date?.message}
            {...form.register('entry_date')}
          />
          <Input
            label="Hours"
            inputMode="decimal"
            required
            placeholder="7.5"
            disabled={frozen}
            error={form.formState.errors.hours?.message}
            {...form.register('hours')}
          />
        </div>

        <Textarea
          label="Notes"
          rows={2}
          hint="What the time went on. This can end up beside the hours on an invoice."
          disabled={frozen}
          error={form.formState.errors.notes?.message}
          {...form.register('notes')}
        />
      </form>
    </Modal>
  );
}
